import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Video, Users, Plus, LogIn, Radio, GraduationCap, Copy, Clock, CircleDot, CheckCircle2, ArrowRight, CalendarClock, ShieldQuestion, UserPlus, ListChecks, X } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { MeetingInvitePanel } from "@/components/meeting/MeetingInvitePanel";

export default function MeetingsListPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [meetings, setMeetings] = useState([]);
  const [title, setTitle] = useState("");
  const [meetingType, setMeetingType] = useState("call");
  const [waitingRoomEnabled, setWaitingRoomEnabled] = useState(false);
  const [scheduleMode, setScheduleMode] = useState(!!location.state?.autoSchedule);
  const [scheduledAt, setScheduledAt] = useState("");
  const [registrationRequired, setRegistrationRequired] = useState(false);
  const [registrationApprovalRequired, setRegistrationApprovalRequired] = useState(false);
  const [customFields, setCustomFields] = useState([]);
  const [creating, setCreating] = useState(false);
  const [joinCode, setJoinCode] = useState("");
  const [joining, setJoining] = useState(false);
  const [createdMeeting, setCreatedMeeting] = useState(null);

  const loadMeetings = () => api.get("/meetings").then((r) => setMeetings(r.data)).catch(() => {});
  useEffect(() => { loadMeetings(); }, []);

  const addCustomField = () => {
    if (customFields.length >= 5) return;
    setCustomFields([...customFields, { key: `field_${Date.now()}`, label: "", required: false }]);
  };
  const updateCustomField = (idx, patch) => setCustomFields(customFields.map((f, i) => (i === idx ? { ...f, ...patch } : f)));
  const removeCustomField = (idx) => setCustomFields(customFields.filter((_, i) => i !== idx));

  const createMeeting = async () => {
    if (scheduleMode && !scheduledAt) { toast.error("Pick a date & time to schedule"); return; }
    setCreating(true);
    try {
      const payload = {
        title: title.trim() || undefined, meeting_type: meetingType, waiting_room_enabled: waitingRoomEnabled,
        registration_required: registrationRequired,
        registration_approval_required: registrationRequired && registrationApprovalRequired,
        registration_fields: registrationRequired ? customFields.filter((f) => f.label.trim()) : [],
      };
      if (scheduleMode) payload.scheduled_at = new Date(scheduledAt).toISOString();
      const { data } = await api.post("/meetings", payload);
      if (scheduleMode) {
        toast.success("Meeting scheduled!");
        loadMeetings();
        setScheduleMode(false);
        setScheduledAt("");
      } else {
        setCreatedMeeting(data);
        loadMeetings();
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not create meeting");
    } finally {
      setCreating(false);
    }
  };

  const joinWithCode = async () => {
    if (!joinCode.trim()) return;
    setJoining(true);
    try {
      const { data } = await api.post("/meetings/join-by-code", { code: joinCode.trim() });
      navigate(`/meeting/${data.id}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Invalid or expired code");
      setJoining(false);
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code).then(() => toast.success("Code copied!")).catch(() => {});
  };

  const statusBadge = (status) => ({
    live: "bg-emerald-400/15 text-emerald-400",
    scheduled: "bg-amber-400/15 text-amber-400",
    ended: "bg-slate-400/15 text-slate-400",
  }[status] || "bg-slate-400/15 text-slate-400");

  return (
    <div className="max-w-4xl mx-auto px-5 sm:px-8 py-8" data-testid="meetings-page">
      <h1 className="font-heading text-2xl font-semibold text-white">Meetings</h1>
      <p className="text-sm text-slate-400 mt-1 max-w-2xl">Start an instant call, schedule one for later, or join with a code.</p>

      {createdMeeting ? (
        <div className="mt-6 space-y-4" data-testid="meeting-created-invite-view">
          <div className="glass-panel rounded-2xl p-6 flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="w-6 h-6 text-emerald-400" />
              <div>
                <p className="text-sm font-semibold text-white">"{createdMeeting.title}" is ready!</p>
                <p className="text-xs text-slate-400">Share the link below, or add people, then enter the meeting.</p>
              </div>
            </div>
            <button onClick={() => navigate(`/meeting/${createdMeeting.id}`)} data-testid="meeting-enter-now-btn"
              className="flex items-center gap-2 bg-cyan-500 hover:bg-cyan-500/90 text-white text-sm font-semibold px-5 py-2.5 rounded-lg transition-colors">
              Enter Meeting <ArrowRight className="w-4 h-4" />
            </button>
          </div>
          <MeetingInvitePanel meetingId={createdMeeting.id} joinCode={createdMeeting.join_code} />
          {createdMeeting.registration_required && (
            <div className="glass-panel rounded-2xl p-5 flex items-center justify-between flex-wrap gap-3" data-testid="meeting-registration-link-panel">
              <div>
                <p className="text-sm font-semibold text-white flex items-center gap-1.5"><UserPlus className="w-4 h-4 text-fuchsia-400" /> Public Registration Link</p>
                <p className="text-xs text-slate-400 mt-1 break-all">{window.location.origin}/meeting/register/{createdMeeting.join_code}</p>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => navigator.clipboard.writeText(`${window.location.origin}/meeting/register/${createdMeeting.join_code}`).then(() => toast.success("Link copied!"))}
                  data-testid="meeting-copy-registration-link-btn" className="text-xs font-semibold bg-white/5 hover:bg-white/10 text-white px-3 py-2 rounded-lg flex items-center gap-1.5"><Copy className="w-3.5 h-3.5" /> Copy Link</button>
                <button onClick={() => navigate(`/meetings/registrations/${createdMeeting.id}`)} data-testid="meeting-manage-registrations-btn"
                  className="text-xs font-semibold bg-fuchsia-500/20 hover:bg-fuchsia-500/30 text-fuchsia-300 px-3 py-2 rounded-lg flex items-center gap-1.5"><ListChecks className="w-3.5 h-3.5" /> Manage Registrations</button>
              </div>
            </div>
          )}
          <button
            onClick={() => {
              setCreatedMeeting(null);
              setTitle("");
              setWaitingRoomEnabled(false);
              setScheduleMode(false);
              setScheduledAt("");
              setRegistrationRequired(false);
              setRegistrationApprovalRequired(false);
              setCustomFields([]);
            }}
            data-testid="meeting-create-another-btn" className="text-xs text-slate-400 hover:text-white"
          >
            ← Start a different meeting
          </button>
        </div>
      ) : (
      <div className="grid sm:grid-cols-2 gap-4 mt-6">
        <div className="glass-panel rounded-2xl p-6" data-testid="meetings-create-panel">
          <h3 className="font-heading font-semibold text-white mb-1 flex items-center gap-2"><Plus className="w-4 h-4 text-cyan-400" /> Start a Meeting</h3>
          <p className="text-sm text-slate-400 mb-4">You'll be the host and get a shareable join code.</p>
          <input
            value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Meeting title (optional)" data-testid="meeting-title-input"
            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white mb-3 focus:outline-none focus:ring-1 focus:ring-cyan-400"
          />
          <div className="flex items-center gap-2 mb-3">
            <button onClick={() => setMeetingType("call")} data-testid="meeting-type-call-btn"
              className={`flex-1 flex items-center justify-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg transition-colors ${meetingType === "call" ? "bg-cyan-500/20 text-cyan-300 border border-cyan-400/30" : "bg-white/5 text-slate-400 border border-transparent"}`}>
              <Users className="w-3.5 h-3.5" /> Interview Call (2-10)
            </button>
            <button onClick={() => setMeetingType("class")} data-testid="meeting-type-class-btn"
              className={`flex-1 flex items-center justify-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-lg transition-colors ${meetingType === "class" ? "bg-fuchsia-500/20 text-fuchsia-300 border border-fuchsia-400/30" : "bg-white/5 text-slate-400 border border-transparent"}`}>
              <GraduationCap className="w-3.5 h-3.5" /> Live Class (10-50+)
            </button>
          </div>
          <label className="flex items-center gap-2 mb-3 text-xs text-slate-400 cursor-pointer select-none">
            <input type="checkbox" checked={waitingRoomEnabled} onChange={(e) => setWaitingRoomEnabled(e.target.checked)} data-testid="meeting-waiting-room-toggle" className="accent-cyan-500" />
            <ShieldQuestion className="w-3.5 h-3.5" /> Enable Waiting Room (you approve each guest before they join)
          </label>
          <label className="flex items-center gap-2 mb-3 text-xs text-slate-400 cursor-pointer select-none">
            <input type="checkbox" checked={registrationRequired} onChange={(e) => setRegistrationRequired(e.target.checked)} data-testid="meeting-registration-toggle" className="accent-cyan-500" />
            <UserPlus className="w-3.5 h-3.5" /> Require Registration (lets people without a Cloudivo account join via a public form)
          </label>
          {registrationRequired && (
            <div className="mb-3 pl-5 border-l border-white/10 space-y-2" data-testid="meeting-registration-options">
              <label className="flex items-center gap-2 text-xs text-slate-400 cursor-pointer select-none">
                <input type="checkbox" checked={registrationApprovalRequired} onChange={(e) => setRegistrationApprovalRequired(e.target.checked)} data-testid="meeting-registration-approval-toggle" className="accent-cyan-500" />
                Require my approval before each registrant can join
              </label>
              {customFields.map((f, idx) => (
                <div key={f.key} className="flex items-center gap-2" data-testid={`meeting-custom-field-row-${idx}`}>
                  <input value={f.label} onChange={(e) => updateCustomField(idx, { label: e.target.value })} placeholder="Custom field label (e.g. Company)"
                    data-testid={`meeting-custom-field-label-${idx}`} className="flex-1 bg-white/5 border border-white/10 rounded-lg px-2.5 py-1.5 text-xs text-white" />
                  <label className="flex items-center gap-1 text-[10px] text-slate-500 cursor-pointer select-none whitespace-nowrap">
                    <input type="checkbox" checked={f.required} onChange={(e) => updateCustomField(idx, { required: e.target.checked })} className="accent-cyan-500" /> req
                  </label>
                  <button onClick={() => removeCustomField(idx)} data-testid={`meeting-custom-field-remove-${idx}`} className="text-slate-500 hover:text-red-400"><X className="w-3.5 h-3.5" /></button>
                </div>
              ))}
              {customFields.length < 5 && (
                <button onClick={addCustomField} data-testid="meeting-add-custom-field-btn" className="text-xs text-cyan-400 hover:text-cyan-300">+ Add custom field</button>
              )}
            </div>
          )}
          <button onClick={() => setScheduleMode((v) => !v)} data-testid="meeting-schedule-toggle-btn" className="flex items-center gap-1.5 text-xs text-cyan-400 hover:text-cyan-300 mb-3">
            <CalendarClock className="w-3.5 h-3.5" /> {scheduleMode ? "Cancel scheduling — start now instead" : "Schedule for later instead of starting now"}
          </button>
          {scheduleMode && (
            <input
              type="datetime-local" value={scheduledAt} onChange={(e) => setScheduledAt(e.target.value)} data-testid="meeting-schedule-datetime-input"
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white mb-3 focus:outline-none focus:ring-1 focus:ring-cyan-400"
            />
          )}
          <button onClick={createMeeting} disabled={creating} data-testid="meeting-create-btn"
            className="w-full flex items-center justify-center gap-2 bg-cyan-500 hover:bg-cyan-500/90 text-white text-sm font-semibold px-5 py-2.5 rounded-lg transition-colors disabled:opacity-60">
            <Radio className="w-4 h-4" /> {creating ? "Please wait..." : scheduleMode ? "Schedule Meeting" : "Start Now"}
          </button>
        </div>

        <div className="glass-panel rounded-2xl p-6" data-testid="meetings-join-panel">
          <h3 className="font-heading font-semibold text-white mb-1 flex items-center gap-2"><LogIn className="w-4 h-4 text-cyan-400" /> Join a Meeting</h3>
          <p className="text-sm text-slate-400 mb-4">Enter the 6-character code shared by your host.</p>
          <div className="flex items-center gap-3">
            <input
              value={joinCode} onChange={(e) => setJoinCode(e.target.value.toUpperCase())} placeholder="ABC123" data-testid="meeting-join-code-input"
              className="flex-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-sm text-white tracking-widest focus:outline-none focus:ring-1 focus:ring-cyan-400"
            />
            <button onClick={joinWithCode} disabled={joining} data-testid="meeting-join-btn"
              className="text-sm font-semibold bg-primary hover:bg-primary/90 text-white px-4 py-2 rounded-lg disabled:opacity-60">Join</button>
          </div>
        </div>
      </div>
      )}

      <h2 className="font-heading text-lg font-semibold text-white mt-10 mb-3">Your Meetings</h2>
      <div className="glass-panel rounded-2xl divide-y divide-white/5" data-testid="meetings-list">
        {meetings.length === 0 && <p className="p-6 text-sm text-slate-400 text-center">No meetings yet — start one above.</p>}
        {meetings.map((m) => (
          <div key={m.id} className="flex items-center gap-3 p-3.5 flex-wrap" data-testid={`meeting-row-${m.id}`}>
            {m.meeting_type === "class" ? <GraduationCap className="w-4 h-4 text-fuchsia-400 shrink-0" /> : <Users className="w-4 h-4 text-cyan-400 shrink-0" />}
            <span className="flex-1 text-sm text-white truncate min-w-[120px]">{m.title}</span>
            {m.scheduled_at && <span className="text-xs text-slate-400 flex items-center gap-1"><CalendarClock className="w-3 h-3" /> {new Date(m.scheduled_at).toLocaleString(undefined, { hour: "numeric", minute: "2-digit", month: "short", day: "numeric" })}</span>}
            <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full flex items-center gap-1 ${statusBadge(m.status)}`}>
              {m.status === "live" && <CircleDot className="w-2.5 h-2.5" />} {m.status}
            </span>
            <span className="text-xs text-slate-400 flex items-center gap-1"><Clock className="w-3 h-3" /> {m.participant_count} joined</span>
            {m.status !== "ended" && (
              <>
                <button onClick={() => copyCode(m.join_code)} data-testid={`meeting-copy-code-${m.id}`} className="flex items-center gap-1 text-xs font-mono text-slate-300 hover:text-white bg-white/5 px-2 py-1 rounded"><Copy className="w-3 h-3" /> {m.join_code}</button>
                {m.registration_required && (
                  <button onClick={() => navigate(`/meetings/registrations/${m.id}`)} data-testid={`meeting-registrations-btn-${m.id}`} className="text-xs font-semibold bg-fuchsia-500/20 text-fuchsia-300 hover:bg-fuchsia-500/30 px-3 py-1.5 rounded-lg flex items-center gap-1"><UserPlus className="w-3 h-3" /> Registrations</button>
                )}
                <button onClick={() => navigate(`/meeting/${m.id}`)} data-testid={`meeting-rejoin-btn-${m.id}`} className="text-xs font-semibold bg-cyan-500/20 text-cyan-300 hover:bg-cyan-500/30 px-3 py-1.5 rounded-lg">Rejoin</button>
              </>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
